import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { MapPin, Phone, Mail, ShieldCheck } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useScrollAnimation, fadeInUp, fadeInLeft, fadeInRight, AnimatedDiv, AnimatedH2 } from "@/hooks/useScrollAnimation";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  fullName: z.string().trim().min(2, "Name must be at least 2 characters").max(100),
  phone: z.string().trim().min(10, "Phone number must be at least 10 digits").max(20),
  email: z.string().trim().email("Invalid email address").max(255),
  interest: z.string().min(1, "Please choose what you need help with"),
});

type FormValues = z.infer<typeof formSchema>;

const Contact = () => {
  const { ref, controls } = useScrollAnimation();
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { fullName: "", phone: "", email: "", interest: "" },
  });


  const onSubmit = async (data: FormValues) => {
    try {
      await fetch("https://services.leadconnectorhq.com/hooks/WXXphothgoBK14OS4P4g/webhook-trigger/wA1AUrmq5w486VzRac6e", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: data.fullName,
          email: data.email,
          phone: data.phone,
          interest: data.interest,
          timestamp: new Date().toISOString(),
          formType: "Find Farm Land",
        }),

      });
      toast({ title: "Message Sent!", description: "We'll get back to you shortly." });
      form.reset();
    } catch {
      toast({ title: "Message Sent!", description: "We'll get back to you shortly." });
      form.reset();
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-accent">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-center">
          <AnimatedDiv
            ref={ref}
            initial="hidden"
            animate={controls}
            variants={fadeInLeft}
          >
            <p className="text-sm font-medium text-primary tracking-[0.3em] uppercase mb-4">Get Started</p>
            <h2 className="text-3xl md:text-5xl heading-luxury text-accent-foreground mb-6">
              Find Farm Land Today
            </h2>
            <div className="w-20 h-0.5 bg-primary mb-8"></div>
            
            <p className="text-white/80 text-lg leading-relaxed mb-8">
              Whether you're looking to buy your first farm, sell agricultural property, or need expert farm management—we're here to help you every step of the way.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-white/80">
                <Phone className="h-5 w-5 text-primary" />
                <a href="tel:+15598719051" className="hover:text-primary transition-colors">(559) 871-9051</a>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <Mail className="h-5 w-5 text-primary" />
                <a href="mailto:gurbirsamran@gmail.com" className="hover:text-primary transition-colors">gurbirsamran@gmail.com</a>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <MapPin className="h-5 w-5 text-primary" />
                <span>Central Valley, California</span>
              </div>
            </div>
          </AnimatedDiv>

          <AnimatedDiv initial="hidden" animate={controls} variants={fadeInRight}>
            <Card className="bg-card border-0 shadow-xl">
              <CardContent className="p-8">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                    <FormField
                      control={form.control}
                      name="fullName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-semibold">Full Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Your full name" {...field} className="h-12 border-border" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-semibold">Phone *</FormLabel>
                          <FormControl>
                            <Input type="tel" placeholder="(559) 123-4567" {...field} className="h-12 border-border" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-semibold">Email *</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="you@example.com" {...field} className="h-12 border-border" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="interest"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-semibold">What can we help with? *</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-12 border-border">
                                <SelectValue placeholder="Select an option" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Buying farmland">Buying farmland</SelectItem>
                              <SelectItem value="Selling my farm">Selling my farm</SelectItem>
                              <SelectItem value="Farm management">Farm management</SelectItem>
                              <SelectItem value="Land valuation">Free land valuation</SelectItem>
                              <SelectItem value="Something else">Something else</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="submit"
                      size="lg"
                      disabled={form.formState.isSubmitting}
                      className="w-full bg-primary text-foreground hover:bg-primary/90 font-semibold text-lg h-12"
                    >
                      {form.formState.isSubmitting ? "Sending..." : "Get My Free Consultation"}
                    </Button>
                    <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground text-center">
                      <ShieldCheck className="h-4 w-4 text-primary" />
                      No obligation. We typically respond the same business day.
                    </p>

                  </form>
                </Form>
              </CardContent>
            </Card>
          </AnimatedDiv>
        </div>
      </div>
    </section>
  );
};

export default Contact;
